import React, { useState, useEffect } from 'react';

import { UserOutlined, SafetyOutlined } from '@ant-design/icons';
import PermissionsDisplay from '@components/shared/permissions/PermissionsDisplay';
import PermissionsEditor from '@components/shared/permissions/PermissionsEditor';
import { useAuth } from '@contexts/admin/AuthContext';
import useCan from '@shared/permissions/useCan';
import ListPageHeader from '@shared/ListPageHeader';
import { Avatar, Card, Space, Spin, message, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams, useLoaderData, useNavigate } from 'react-router-dom';

import { permissionTemplateService } from '@services/admin/permissionTemplateService';
import { userService } from '@services/admin/userService';

export default function AdminUserEdit() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const loaderData = useLoaderData();
  const can = useCan();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(loaderData || null);

  const isSelf = user && user.id === currentUser?.id;
  // 自編走 Profile 頁；Edit 頁本身只給「別人編你」的場景
  useEffect(() => {
    if (isSelf) navigate('/profile', { replace: true });
  }, [isSelf, navigate]);

  const canEditPermissions = can('users:write') && user?.id !== currentUser?.id;

  useEffect(() => {
    if (!loaderData) fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, loaderData]);

  const fetchUser = async () => {
    setLoading(true);
    try {
      const result = await userService.fetchUser(id);
      if (result.success && result.data) setUser(result.data);
      else message.error(result.message || t('common.error.fetch_failed'));
    } catch (e) {
      console.error(e);
      message.error(t('common.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSavePermissions = async ({ role, permissions }) => {
    try {
      const result = role
        ? await userService.setRole(id, role)
        : await userService.updatePermissions(id, permissions);
      if (result.success) {
        await fetchUser();
        return Promise.resolve();
      }
      return Promise.reject(new Error(result.message));
    } catch (e) {
      return Promise.reject(e);
    }
  };

  if (loading || !user) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  const isDisabled = Boolean(user.disabled_at);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
      <ListPageHeader basePath="/admin" items={['settings', 'settings.users', { label: user.name || user.email }]} />

      {/* Identity Card */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Avatar size={56} icon={<UserOutlined />} />
          <div style={{ flex: 1 }}>
            <Space size={12} align="center" style={{ marginBottom: 4 }}>
              <Typography.Text strong style={{ fontSize: 18 }}>{user.name || '-'}</Typography.Text>
              {isDisabled ? (
                <Tag color="error">{t('admin.users.edit.account_status.disabled')}</Tag>
              ) : (
                <Tag color="success">{t('admin.users.edit.account_status.active')}</Tag>
              )}
            </Space>
            <div>
              <Typography.Text type="secondary">{user.email || '-'}</Typography.Text>
            </div>
            {(user.created_by || user.updated_by || user.disabled_at) && (
              <div style={{ marginTop: 6, fontSize: 12 }}>
                <Space size={12} wrap>
                  {user.created_by ? (
                    <Typography.Text type="secondary">
                      {t('common.audit.created_by_inviter', { defaultValue: '由' })} {user.created_by.name} {t('common.audit.invited', { defaultValue: '邀請' })} · {new Date(user.created_at).toLocaleDateString()}
                    </Typography.Text>
                  ) : (
                    <Typography.Text type="secondary">
                      {t('common.audit.created_at', { defaultValue: '建立於' })} {new Date(user.created_at).toLocaleDateString()}
                    </Typography.Text>
                  )}
                  {user.updated_by && (
                    <Typography.Text type="secondary">
                      {t('common.audit.last_updated_by', { defaultValue: '最後修改' })} {user.updated_by.name} · {new Date(user.updated_at).toLocaleString()}
                    </Typography.Text>
                  )}
                  {user.disabled_at && (
                    <Typography.Text type="danger">
                      {t('common.audit.disabled_at', { defaultValue: '已停用' })} · {new Date(user.disabled_at).toLocaleString()}
                    </Typography.Text>
                  )}
                </Space>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Permissions — rely on the editor's own Card when editable */}
      {canEditPermissions ? (
        <div style={{ marginBottom: 16 }}>
          <PermissionsEditor
            permissions={user?.permissions || []}
            onSave={handleSavePermissions}
            templateService={permissionTemplateService}
            role="admin"
            canEdit={!isSelf}
            isEditingSelf={isSelf}
          />
        </div>
      ) : (
        <Card
          title={<Space><SafetyOutlined />{t('admin.users.edit.permissions_title')}</Space>}
          style={{ marginBottom: 16 }}
        >
          <PermissionsDisplay permissions={user?.permissions || []} showTitle={false} role="admin" />
        </Card>
      )}
    </div>
  );
}
